import moment from "moment";
import { capitalizeAllLetters } from "Utils/utils";

import {
  CARE_WATCH_STATUSES,
  CARE_WATCH_VIEWS,
  RISK_LEVELS,
  isCareWatchLiveView,
} from "./constants";
import { selectedCategories } from "./queueUtils";

/** Server values for CareWatchSignalResource::reportStatus. */
const REPORT_STATUSES = ["reported", "possibly_reported", "not_reported"];

/**
 * Hours a signal may sit before the dashboard treats it as overdue.
 * Presentation policy, so it lives here rather than in the API payload.
 */
export const CARE_WATCH_SLA_HOURS = 48;

const nameParts = (displayName = "") => {
  const parts = String(displayName).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { firstName: "", lastName: "", initials: "—" };
  const [firstName, ...rest] = parts;
  const lastName = rest.join(" ");
  const initials = `${firstName[0] || ""}${(lastName[0] || "")}`.toUpperCase();
  return { firstName, lastName, initials };
};

/** Title-case a person name for table and detail display. */
export const formatPersonName = (value = "") =>
  capitalizeAllLetters(String(value || "").trim());

/** Lift the first letter of a note or summary. The rest of the wording stays. */
export const formatCareWatchSentence = (value = "") => {
  const text = String(value || "").trim();
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * Live + signal people may arrive as a display string or as a person object.
 * AvatarGroup needs first_name / last_name / photo; the table also uses camelCase.
 */
export const adaptLiveParticipant = (person) => {
  if (person == null || person === "") {
    return { firstName: "", lastName: "", first_name: "", last_name: "", initials: "—" };
  }
  if (typeof person === "string") {
    const parts = nameParts(person);
    const firstName = formatPersonName(parts.firstName);
    const lastName = formatPersonName(parts.lastName);
    return {
      firstName,
      lastName,
      first_name: firstName,
      last_name: lastName,
      initials: parts.initials,
    };
  }
  const firstName = formatPersonName(person.firstName || person.first_name || "");
  const lastName = formatPersonName(person.lastName || person.last_name || "");
  return {
    id: person.id,
    uuid: person.uuid,
    firstName,
    lastName,
    first_name: person.first_name || firstName,
    last_name: person.last_name || lastName,
    photo: person.photo || person.profile_picture || null,
    profile_picture: person.profile_picture || person.photo || null,
    status: person.status,
    initials: `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase() || "—",
  };
};

const peopleFromRow = (row = {}) =>
  livePeopleFromRow(row)
    .map(adaptLiveParticipant)
    .filter((person) => person.firstName || person.lastName || person.uuid || person.id);

const livePeopleFromRow = (row = {}) => {
  const list = Array.isArray(row.participants) ? row.participants : [];
  const refs = Array.isArray(row.participantRefs) ? row.participantRefs : [];
  const first = list[0];
  // `null` is typeof "object" — don't treat empty names as structured people
  // and skip the refs the detail endpoint actually sends.
  if (first && typeof first === "object") return list;
  if (refs.length) return refs;
  return list;
};

const elapsedLabel = (from) => {
  if (!from) return "";
  const hours = moment().diff(moment(from), "hours");
  if (hours < 24) return `${Math.max(hours, 0)}h`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
};

const toReadinessScore = (row) => {
  const raw = Number(row.readinessScore ?? row.readiness_score);
  return Number.isFinite(raw) ? raw : null;
};

const validMoment = (value) => {
  if (!value) return null;
  const parsed = moment(value);
  return parsed.isValid() ? parsed : null;
};

/**
 * When the 48 hour follow-up window opens.
 *
 * The later of the analysis stamp and the shift end. An open Needs follow-up
 * row whose analysis was written before the shift finished was counted on the
 * Overdue tile as soon as that old stamp passed 48 hours.
 */
const slaClockStart = (row = {}) => {
  const analysis = validMoment(
    row.analyzedAt || row.analyzed_updated_at || row.analyzed_at
  );
  const shiftEnd =
    validMoment(row.workerEndTime || row.worker_end_time) ||
    validMoment(row.shiftDate || row.shift_date)?.endOf("day") ||
    null;

  if (analysis && shiftEnd) return moment.max(analysis, shiftEnd);
  return analysis || shiftEnd;
};

/**
 * Map one API signal onto the shape the dashboard components render.
 *
 * The API returns analysis facts; anything time-relative or purely visual
 * (elapsed SLA, initials, short date label) is derived here so the payload
 * stays stable and cacheable.
 */
export const adaptCareWatchSignal = (row = {}) => {
  const clock = slaClockStart(row);
  const elapsedHours =
    clock && clock.isBefore(moment()) ? moment().diff(clock, "hours") : 0;
  const workflow = [
    CARE_WATCH_STATUSES.ACKNOWLEDGED,
    CARE_WATCH_STATUSES.PROMOTED,
    CARE_WATCH_STATUSES.NEEDS_FOLLOW_UP,
  ].includes(row.status)
    ? row.status
    : CARE_WATCH_STATUSES.NEEDS_FOLLOW_UP;
  const promoted = Boolean(row.promoted) || workflow === CARE_WATCH_STATUSES.PROMOTED;
  const overdue =
    workflow === CARE_WATCH_STATUSES.NEEDS_FOLLOW_UP &&
    elapsedHours > CARE_WATCH_SLA_HOURS;

  return {
    ...row,
    id: row.id,
    timesheetId: row.timesheetId,
    view: Object.values(CARE_WATCH_VIEWS).includes(row.view)
      ? row.view
      : CARE_WATCH_VIEWS.DOCUMENTATION,
    risk: row.risk === "medium"
      ? RISK_LEVELS.MODERATE
      : Object.values(RISK_LEVELS).includes(row.risk)
        ? row.risk
        : RISK_LEVELS.LOW,

    // Workflow is persisted on care_watch_signals. Overdue is only a flag on
    // unactioned rows — it must not replace acknowledged / promoted.
    status: workflow,
    promoted,

    // List rows send display-name strings; the detail payload also has
    // participantRefs with ids/photos. Prefer structured people so the
    // right panel can render initials and names.
    participants: peopleFromRow(row).map(adaptLiveParticipant),
    shiftDateLabel: row.shiftDate ? moment(row.shiftDate).format("D MMM") : "",
    shiftTimeLabel: formatTimeRange(row.workerStartTime, row.workerEndTime),
    readinessScore: toReadinessScore(row),

    slaHours: CARE_WATCH_SLA_HOURS,
    slaElapsed: elapsedLabel(clock && clock.isBefore(moment()) ? clock : null),
    overdue,

    // Has this shift's incident already been written up? Guards against a
    // manager filing a second form for the same event. "possibly" is an
    // unlinked report matched on participant and time, so it warns rather
    // than blocks — see CareWatchSignalService::annotatePossiblyReported.
    reportStatus: REPORT_STATUSES.includes(row.reportStatus)
      ? row.reportStatus
      : "not_reported",
    reportedIncidentId: row.reportedIncidentId || null,
    reportedIncidentCount: Number(row.reportedIncidentCount) || 0,

    // A manager opened the incident form but has not filed it. The signal
    // deliberately stays open until an incident exists.
    promoteStarted: Boolean(row.promoteStarted),
    issues: Array.isArray(row.issues) && row.issues.length
      ? row.issues
      : row.issue
        ? [row.issue]
        : [],
    participantName: formatPersonName(row.participantName || ""),
    workerName: formatPersonName(row.workerName || row.worker || ""),
    notePreview: formatCareWatchSentence(row.notePreview || ""),
    shiftNote: formatCareWatchSentence(row.shiftNote || ""),
  };
};

export const adaptCareWatchSignals = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map(adaptCareWatchSignal);

/**
 * Adapt one Live API row. The server already computes liveState, clockInLabel,
 * etc. People are normalised so AvatarGroup and capitalized name cells work.
 */
export const adaptCareWatchLiveRow = (row = {}) => {
  const workerName =
    typeof row.worker === "string"
      ? formatPersonName(row.worker)
      : row.worker;

  return {
    ...row,
    view: CARE_WATCH_VIEWS.LIVE,
    worker: workerName,
    noteSoFar: formatCareWatchSentence(row.noteSoFar || row.note_so_far || ""),
    workerRef: row.workerRef
      ? {
          ...row.workerRef,
          firstName: formatPersonName(row.workerRef.firstName || ""),
          lastName: formatPersonName(row.workerRef.lastName || ""),
        }
      : row.workerRef,
    participants: peopleFromRow(row).map(adaptLiveParticipant),
    shiftDateLabel: liveShiftDateLabel(row),
    shiftTimeLabel:
      row.shiftTimeLabel ||
      formatTimeRange(row.workerStartTime, row.workerEndTime),
  };
};

export const adaptCareWatchLiveRows = (rows = []) => {
  // The web path receives a plain array. Mobile pagination returns
  // { items, total, has_more } — if that envelope ever reaches the web
  // adapter, unwrap it so the table does not render empty.
  const list = Array.isArray(rows)
    ? rows
    : Array.isArray(rows?.items)
      ? rows.items
      : [];
  return list.map(adaptCareWatchLiveRow);
};

/** "28 Sep 2026", or "28 Sep 2026 – 29 Sep 2026" when the window crosses midnight. */
export const liveShiftDateLabel = (row = {}) => {
  const start =
    liveDayLabel(row.workerStartTime) || liveDayLabel(row.shiftDate);
  const end = liveDayLabel(row.workerEndTime);
  if (!start) return "";
  if (end && end !== start) return `${start} – ${end}`;
  return start;
};

const liveDayLabel = (value) => {
  if (!value) return "";
  const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return "";
  const date = moment(match[0], "YYYY-MM-DD", true);
  return date.isValid() ? date.format("DD MMM YYYY") : "";
};

const nameOfPerson = (person) => {
  if (!person) return "";
  if (typeof person === "string") return formatPersonName(person);
  return formatPersonName(
    `${person.firstName || person.first_name || ""} ${
      person.lastName || person.last_name || ""
    }`.trim()
  );
};

/** Full display names for adapted participant rows or plain strings. */
export const participantNameList = (people = []) =>
  (Array.isArray(people) ? people : [])
    .map((person) => nameOfPerson(person))
    .filter(Boolean);

/** Display name for a participant list, collapsing extras to "+N". */
export const participantDisplayName = (people = []) => {
  if (!people.length) return "—";
  if (people.length === 1) return nameOfPerson(people[0]) || "—";
  const primary = nameOfPerson(people[0]);
  return primary ? `${primary}  +${people.length - 1}` : "—";
};

/**
 * UI filter state to the API's parameter names.
 *
 * The listing takes the full set. Overview omits `risk`, `issue`, and
 * `live_state` so the tiles keep showing the breakdown you can click into,
 * rather than collapsing to the one bucket already selected.
 */
export const toApiFilters = (filters = {}, options = {}) => {
  const omit = new Set(options.omit || []);

  if (isCareWatchLiveView(filters.view)) {
    // Live is today's floor. The week range belongs to the other tabs.
    const liveParams = {
      live_state: filters.liveState,
      search: (filters.search || "").trim(),
      // Live stays on today's floor by default. Do not send the incident
      // week range — an accepted Sunday shift belongs on Sunday, not today.
    };
    return Object.fromEntries(
      Object.entries(liveParams).filter(([key, value]) => {
        if (omit.has(key)) return false;
        return value !== undefined && value !== null && value !== "" && value !== "all";
      })
    );
  }

  const isDocView = filters.view === CARE_WATCH_VIEWS.DOCUMENTATION;
  const status =
    isDocView && filters.status === CARE_WATCH_STATUSES.PROMOTED
      ? CARE_WATCH_STATUSES.NEEDS_FOLLOW_UP
      : filters.status;
  const params = {
    view: filters.view,
    status,
    category: isDocView ? undefined : selectedCategories(filters.category),
    risk: isDocView ? undefined : filters.risk,
    issue: filters.issue,
    from_date: filters.fromDate,
    to_date: filters.toDate,
    search: (filters.search || "").trim(),
  };

  return Object.fromEntries(
    Object.entries(params).filter(([key, value]) => {
      if (omit.has(key)) return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return value !== undefined && value !== null && value !== "" && value !== "all";
    })
  );
};

/**
 * Dates from the API are timestamps ("2026-08-11 00:00:00"), not bare dates.
 *
 * Parsing them by appending a time produced "2026-08-11 00:00:00T00:00:00",
 * which is an Invalid Date — and because the helper was copied into two
 * components, fixing one left the other still rendering the words "Invalid
 * Date" in the detail header. One implementation, used everywhere.
 */
export const parseApiDate = (value) => {
  if (!value) return null;
  const datePart = String(value).trim().split(/[ T]/)[0];
  const date = new Date(`${datePart}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "11 Aug" — the short form used on cards and previews. */
export const formatShortDate = (value) => {
  const date = parseApiDate(value);
  return date
    ? date.toLocaleDateString("en-AU", { day: "numeric", month: "short" })
    : "";
};

/** "11 Aug 2026" — the long form used in the detail header. */
export const formatLongDate = (value) => {
  const date = parseApiDate(value);
  return date
    ? date.toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";
};

/** "12:00am – 10:45am", or "" when the shift has no worked window. */
export const formatTimeRange = (from, to) => {
  const opts = { hour: "numeric", minute: "2-digit", hour12: true };
  const start = parseApiTime(from);
  const end = parseApiTime(to);
  if (!start || !end) return "";
  const fmt = (d) =>
    d.toLocaleTimeString("en-AU", opts).replace(/\s/g, "").toLowerCase();
  return `${fmt(start)} – ${fmt(end)}`;
};

const parseApiTime = (value) => {
  if (!value) return null;
  const date = new Date(String(value).trim().replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? null : date;
};
